import { useState, useEffect } from 'react'
import { useLocation, Link } from 'react-router-dom'
import {
  Rocket,
  LayoutDashboard,
  CalendarCheck,
  Car,
  FileText,
  Settings,
  Globe,
  ChevronRight,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { ScrollArea } from '@/components/ui/scroll-area'
import sidebarData from '@/docs/_sidebar.json'
import type { DocsNavigation, DocsSidebarSection } from '@/types/docs'

// Map icon names to Lucide components
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Rocket,
  LayoutDashboard,
  CalendarCheck,
  Car,
  FileText,
  Settings,
  Globe,
}

interface DocsSidebarProps {
  basePath: string
  className?: string
}

const STORAGE_KEY = 'docs-sidebar-expanded'

/**
 * Documentation sidebar navigation component
 */
export function DocsSidebar({ basePath, className }: DocsSidebarProps) {
  const location = useLocation()
  const navigation = sidebarData as DocsNavigation

  // Get current doc path from URL
  const getCurrentPath = () => {
    const docsMatch = location.pathname.match(/\/docs\/(.+)$/)
    return docsMatch ? docsMatch[1] : 'getting-started/index'
  }

  const currentPath = getCurrentPath()

  // Initialize expanded sections from localStorage or based on current path
  const [expandedSections, setExpandedSections] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        return JSON.parse(saved)
      }
    } catch {
      // Ignore localStorage errors
    }
    // Default: expand section containing current page
    const currentSection = navigation.sections.find((section) =>
      section.items.some((item) => item.path === currentPath)
    )
    return currentSection ? [currentSection.title] : [navigation.sections[0]?.title]
  })

  // Save expanded sections to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(expandedSections))
    } catch {
      // Ignore localStorage errors
    }
  }, [expandedSections])

  // Expand section containing current page when path changes
  useEffect(() => {
    const currentSection = navigation.sections.find((section) =>
      section.items.some((item) => item.path === currentPath)
    )
    if (currentSection && !expandedSections.includes(currentSection.title)) {
      setExpandedSections((prev) => [...prev, currentSection.title])
    }
  }, [currentPath, navigation.sections, expandedSections])

  const toggleSection = (title: string) => {
    setExpandedSections((prev) =>
      prev.includes(title)
        ? prev.filter((t) => t !== title)
        : [...prev, title]
    )
  }

  return (
    <ScrollArea className={cn('h-full', className)}>
      <nav className="py-4 pr-4" aria-label="Documentation navigation">
        {navigation.sections.map((section) => (
          <SidebarSection
            key={section.title}
            section={section}
            basePath={basePath}
            currentPath={currentPath}
            isExpanded={expandedSections.includes(section.title)}
            onToggle={() => toggleSection(section.title)}
          />
        ))}
      </nav>
    </ScrollArea>
  )
}

interface SidebarSectionProps {
  section: DocsSidebarSection
  basePath: string
  currentPath: string
  isExpanded: boolean
  onToggle: () => void
}

function SidebarSection({
  section,
  basePath,
  currentPath,
  isExpanded,
  onToggle,
}: SidebarSectionProps) {
  const Icon = iconMap[section.icon] || FileText

  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle} className="mb-2">
      <CollapsibleTrigger className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors">
        <Icon className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-left">{section.title}</span>
        <ChevronRight
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
            isExpanded && 'rotate-90'
          )}
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
        <ul className="ml-4 mt-1 space-y-1 border-l pl-4">
          {section.items.map((item) => {
            const isActive = currentPath === item.path
            return (
              <li key={item.path}>
                <Link
                  to={`${basePath}/${item.path}`}
                  className={cn(
                    'block rounded-md px-3 py-1.5 text-sm transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground font-medium'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  {item.title}
                </Link>
              </li>
            )
          })}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  )
}
